import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  useClubDetails,
  useClubGatherings,
  type ListedGathering,
} from "@/lib/clubs/gatherings";
import { useVotingSession, apiPhaseToUiPhase } from "@/lib/clubs/voting";
import { clubAccent, clubInitials } from "@/lib/clubs/clubAccent";
import { relativeDay, shortDate, shortTime } from "@/lib/format";
import styles from "./ClubHome.module.scss";

interface Props {
  clubId: string;
}

const TABS = ["Overview", "Gatherings", "Voting", "Rooms"] as const;
type Tab = (typeof TABS)[number];

const PHASE_LABEL: Record<ReturnType<typeof apiPhaseToUiPhase>, string> = {
  SETUP: "No active session",
  NOMINATING: "Nominating phase",
  RANKING: "Ranking phase",
  CLOSED: "Winner picked",
};

export function ClubHome({ clubId }: Props) {
  const [tab, setTab] = useState<Tab>("Overview");
  const detailsQuery = useClubDetails(clubId);
  const gatheringsQuery = useClubGatherings(clubId, { upcoming: true });
  const votingQuery = useVotingSession(clubId);

  const details = detailsQuery.data;

  if (detailsQuery.isLoading && !details) {
    return (
      <div className={styles.loadingShell} role="status" aria-live="polite">
        Loading club…
      </div>
    );
  }

  if (!details) {
    return <div className={styles.loadingShell}>Couldn't load this club.</div>;
  }

  const accent = clubAccent(clubId);
  const initials = clubInitials(details.name);
  const upcoming = (gatheringsQuery.data?.gatherings ?? [])
    .filter((g) => !g.cancelledAt)
    .sort((a, b) => a.date.localeCompare(b.date));
  const session = votingQuery.data ?? null;
  const phase = apiPhaseToUiPhase(session?.phase);
  const activeRoom = details.readingRooms.find((r) => r.status === "ACTIVE");

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <nav className={styles.crumbs} aria-label="Breadcrumbs">
          <Link to="/" className={styles.crumbLink}>
            Your clubs
          </Link>
          <span className={styles.crumbSep}>/</span>
          <span className={styles.crumbCurrent}>{details.name}</span>
        </nav>

        <div className={styles.titleRow}>
          <span
            className={`${styles.cover} ${styles[`cover_${accent}`]}`}
            aria-hidden
          >
            {details.image ? (
              <img className={styles.coverImage} src={details.image} alt="" />
            ) : (
              initials
            )}
          </span>
          <div>
            <h1 className={styles.title}>{details.name}</h1>
            <p className={styles.tagline}>
              {[
                details.description,
                details.memberCount ? `${details.memberCount} members` : null,
                details.city,
              ]
                .filter(Boolean)
                .join(" · ") || "Club"}
            </p>
          </div>
          <div className={styles.titleActions}>
            <Link
              to="/clubs/$id/gatherings/new"
              params={{ id: clubId }}
              className={styles.btn}
            >
              Schedule gathering
            </Link>
            <Link
              to="/clubs/$id/rooms/new"
              params={{ id: clubId }}
              className={`${styles.btn} ${styles.btnPrimary}`}
            >
              New reading room
            </Link>
          </div>
        </div>

        <nav className={styles.tabs} role="tablist" aria-label="Sections">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`${styles.tab} ${tab === t ? styles.tabActive : ""}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </nav>
      </header>

      <section className={styles.body}>
        {tab === "Overview" && (
          <OverviewTab
            clubId={clubId}
            currentBookTitle={activeRoom?.book?.title ?? null}
            currentBookAuthor={activeRoom?.book?.author ?? null}
            activeRoomId={activeRoom?.id ?? null}
            phaseLabel={PHASE_LABEL[phase]}
            nominationCount={session?.nominations.length ?? 0}
            memberCount={session?.memberCount ?? details.memberCount}
            nextGathering={upcoming[0] ?? null}
            roomCount={details.readingRooms.length}
          />
        )}

        {tab === "Gatherings" && (
          <GatheringsTab clubId={clubId} gatherings={upcoming} />
        )}

        {tab === "Voting" && (
          <VotingTab
            clubId={clubId}
            phaseLabel={PHASE_LABEL[phase]}
            nominations={session?.nominations ?? []}
          />
        )}

        {tab === "Rooms" && (
          <RoomsTab clubId={clubId} rooms={details.readingRooms} />
        )}
      </section>
    </div>
  );
}

interface OverviewProps {
  clubId: string;
  currentBookTitle: string | null;
  currentBookAuthor: string | null;
  activeRoomId: string | null;
  phaseLabel: string;
  nominationCount: number;
  memberCount: number;
  nextGathering: ListedGathering | null;
  roomCount: number;
}

function OverviewTab({
  clubId,
  currentBookTitle,
  currentBookAuthor,
  activeRoomId,
  phaseLabel,
  nominationCount,
  memberCount,
  nextGathering,
  roomCount,
}: OverviewProps) {
  return (
    <div className={styles.overview}>
      <article className={styles.cardLarge}>
        <span className={styles.cardLabel}>Currently reading</span>
        {currentBookTitle ? (
          <>
            <h2 className={styles.book}>{currentBookTitle}</h2>
            <p className={styles.bookMeta}>
              {currentBookAuthor ?? "Unknown author"}
            </p>
            <p className={styles.tinySub}>
              {roomCount} room{roomCount === 1 ? "" : "s"} in this club
            </p>
          </>
        ) : (
          <>
            <h2 className={styles.book}>No active room yet</h2>
            <p className={styles.bookMeta}>
              Pick a winner in voting, then start a reading room.
            </p>
          </>
        )}
        {activeRoomId ? (
          <Link
            to="/clubs/$id/rooms/$roomId"
            params={{ id: clubId, roomId: activeRoomId }}
            className={styles.cardLink}
          >
            Open facilitator dashboard →
          </Link>
        ) : (
          <Link
            to="/clubs/$id/rooms/new"
            params={{ id: clubId }}
            className={styles.cardLink}
          >
            Start a reading room →
          </Link>
        )}
      </article>

      <Link
        to="/clubs/$id/voting"
        params={{ id: clubId }}
        className={styles.card}
      >
        <span className={styles.cardLabel}>Voting</span>
        <h3 className={styles.cardValue}>{phaseLabel}</h3>
        <p className={styles.cardMeta}>
          {nominationCount} nomination{nominationCount === 1 ? "" : "s"} ·{" "}
          {memberCount} eligible
        </p>
        <span className={styles.cardLink}>Open voting →</span>
      </Link>

      <Link
        to="/clubs/$id/gatherings"
        params={{ id: clubId }}
        className={styles.card}
      >
        <span className={styles.cardLabel}>Next gathering</span>
        {nextGathering ? (
          <>
            <h3 className={styles.cardValue}>
              {relativeDay(nextGathering.date)}
            </h3>
            <p className={styles.cardMeta}>
              {nextGathering.title} · {shortTime(nextGathering.date)}
            </p>
            <p className={styles.tinySub}>
              {[nextGathering.venueName, nextGathering.neighborhood]
                .filter(Boolean)
                .join(" · ") || "Venue TBD"}
            </p>
          </>
        ) : (
          <>
            <h3 className={styles.cardValue}>Nothing planned</h3>
            <p className={styles.cardMeta}>
              Schedule one to give members a date.
            </p>
          </>
        )}
        <span className={styles.cardLink}>Open gatherings →</span>
      </Link>
    </div>
  );
}

interface GatheringsTabProps {
  clubId: string;
  gatherings: ListedGathering[];
}

function GatheringsTab({ clubId, gatherings }: GatheringsTabProps) {
  if (gatherings.length === 0) {
    return (
      <div className={styles.list}>
        <h2 className={styles.listHead}>Scheduled · 0</h2>
        <p className={styles.listEmpty}>Nothing on the calendar.</p>
      </div>
    );
  }
  return (
    <div className={styles.list}>
      <h2 className={styles.listHead}>Scheduled · {gatherings.length}</h2>
      {gatherings.map((g) => {
        const yes = g.rsvpCounts.GOING ?? 0;
        const cap = g.capacity ?? null;
        return (
          <Link
            key={g.id}
            to="/clubs/$id/gatherings/$gid"
            params={{ id: clubId, gid: g.id }}
            className={styles.listRow}
          >
            <span className={styles.listDate}>
              {shortDate(g.date)}
              <span className={styles.listTime}>{shortTime(g.date)}</span>
            </span>
            <span className={styles.listBody}>
              <span className={styles.listTitle}>{g.title}</span>
              <span className={styles.listMeta}>
                {[g.venueName, g.neighborhood].filter(Boolean).join(" · ") ||
                  "Venue TBD"}
              </span>
            </span>
            <span className={styles.listRsvp}>
              {yes}
              {cap ? `/${cap}` : ""} yes
            </span>
          </Link>
        );
      })}
    </div>
  );
}

interface VotingTabProps {
  clubId: string;
  phaseLabel: string;
  nominations: ReturnType<typeof useVotingSession>["data"] extends infer R
    ? R extends { nominations: infer N }
      ? N
      : never
    : never;
}

function VotingTab({ clubId, phaseLabel, nominations }: VotingTabProps) {
  return (
    <div className={styles.voting}>
      <h2 className={styles.listHead}>
        {phaseLabel} · {nominations.length} nomination
        {nominations.length === 1 ? "" : "s"}
      </h2>
      {nominations.length === 0 ? (
        <p className={styles.listEmpty}>
          No nominations yet.{" "}
          <Link
            to="/clubs/$id/voting"
            params={{ id: clubId }}
            className={styles.cardLink}
          >
            Open voting →
          </Link>
        </p>
      ) : (
        <ol className={styles.nomList}>
          {nominations.map((n, i) => (
            <li key={n.id} className={styles.nomRow}>
              <span className={styles.nomRank}>{i + 1}</span>
              <span className={styles.nomBody}>
                <span className={styles.nomTitle}>{n.book.title}</span>
                <span className={styles.nomMeta}>
                  {n.book.author ?? "Unknown author"}
                </span>
              </span>
              <span className={styles.nomSupport}>
                <span className={styles.nomSupportValue}>1</span>
                <span className={styles.nomSupportLabel}>
                  by {n.user.displayName ?? "member"}
                </span>
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

interface RoomsTabProps {
  clubId: string;
  rooms: NonNullable<ReturnType<typeof useClubDetails>["data"]>["readingRooms"];
}

function RoomsTab({ clubId, rooms }: RoomsTabProps) {
  if (rooms.length === 0) {
    return (
      <div className={styles.list}>
        <h2 className={styles.listHead}>Reading rooms · 0</h2>
        <p className={styles.listEmpty}>
          No rooms yet.{" "}
          <Link
            to="/clubs/$id/rooms/new"
            params={{ id: clubId }}
            className={styles.cardLink}
          >
            Start the first one →
          </Link>
        </p>
      </div>
    );
  }
  return (
    <div className={styles.list}>
      <h2 className={styles.listHead}>Reading rooms · {rooms.length}</h2>
      {rooms.map((r) => (
        <Link
          key={r.id}
          to="/clubs/$id/rooms/$roomId"
          params={{ id: clubId, roomId: r.id }}
          className={styles.listRow}
        >
          <span className={styles.listDate}>{r.status}</span>
          <span className={styles.listBody}>
            <span className={styles.listTitle}>
              {r.book?.title ?? r.name ?? "Reading room"}
            </span>
            <span className={styles.listMeta}>
              {r.book?.author ?? "Unknown author"}
            </span>
          </span>
          <span className={styles.listRsvp}>
            {r.memberCount ?? 0} reader{r.memberCount === 1 ? "" : "s"}
          </span>
        </Link>
      ))}
    </div>
  );
}
